<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="客户名称" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择客户"
        >
          <el-option
            v-for="item in customerList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="分类" prop="category">
        <el-select
          v-model="queryParams.category"
          class="!w-200px"
          clearable
          placeholder="请选择分类"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_INTERVIEW_CATEGORY)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="销售" prop="salesUserId">
        <el-select
          v-model="queryParams.salesUserId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择销售"
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="面谈时间" prop="interviewTimes">
        <el-date-picker
          v-model="queryParams.interviewTimes"
          :default-time="defaultTime"
          class="!w-360px"
          end-placeholder="结束日期"
          range-separator="-"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button v-hasPermi="['crm:interview:create']" type="primary" @click="openForm('create')">
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:interview:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        fixed="left"
        label="面谈时间"
        prop="interviewTime"
        width="180"
      />
      <el-table-column align="center" label="分类" prop="category" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_INTERVIEW_CATEGORY" :value="scope.row.category" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="所在地" prop="areaName" width="180" />
      <el-table-column align="center" label="客户名称" prop="customerName" width="180" />
      <el-table-column align="center" label="销售" prop="salesUserName" width="110" />
      <el-table-column align="center" label="参与人员" prop="participantUserNames" width="200" />
      <el-table-column align="center" label="需要准备" prop="prepare" width="200" />
      <el-table-column align="center" label="备注" prop="remark" width="180" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-followup-action">
            <el-button
              v-hasPermi="['crm:interview:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:interview:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <InterviewForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import { InterviewApi, InterviewVO } from '@/api/crm/interview'
import * as CustomerApi from '@/api/crm/customer'
import * as UserApi from '@/api/system/user'
import InterviewForm from './InterviewForm.vue'

defineOptions({ name: 'CrmInterview' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<InterviewVO[]>([]) // 列表的数据
const customerList = ref<any[]>([]) // 客户下拉
const userOptions = ref<UserApi.UserVO[]>([]) // 用户下拉
const exportLoading = ref(false) // 导出的加载中
/** 日期范围默认带时分秒 00:00:00 ~ 23:59:59 */
const defaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined,
  category: undefined,
  salesUserId: undefined,
  interviewTimes: []
})
const queryFormRef = ref() // 搜索的表单

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await InterviewApi.getInterviewPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.customerId = undefined
  queryParams.category = undefined
  queryParams.salesUserId = undefined
  queryParams.interviewTimes = []
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof InterviewForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await InterviewApi.deleteInterview(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await InterviewApi.exportInterview(queryParams)
    download.excel(data, '客户面谈.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  customerList.value = await CustomerApi.getCustomerSimpleList()
  userOptions.value = await UserApi.getSimpleUserList()
  await getList()
})
</script>

<style lang="scss" scoped>
.crm-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
